import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, Card, CardContent, Grid, Chip, Avatar, LinearProgress } from "@mui/material";
import { tokens } from "../../theme";
import { useTheme } from "@emotion/react";
import DoctorService from "../../services/doctorService";
import Groups2Icon from "@mui/icons-material/Groups2";
import FileService from "../../services/doctorServices/FileService";
import FolderIcon from "@mui/icons-material/Folder";
import PatientService from "../../services/doctorServices/patientService";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import FormService from "../../services/doctorServices/FormService";
import DescriptionIcon from "@mui/icons-material/Description";
import GenderPieChart from "../../components/GenderPieChart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useNavigate } from "react-router-dom";
import Headeer from "../../components/Headeer";
// Stats Components
import StatsFilters, { ENTITIES } from "../../components/Stats/StatsFilters";
import StatsChart from "../../components/Stats/StatsChart";
import StatsSummaryCards from "../../components/Stats/StatsSummaryCards";
// Stats Service
import StatsService from "../../services/doctorServices/StatsService";

export default function Dashboard() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation(['dashboard', 'stats']);
  const navigate = useNavigate();
  const doctorId = localStorage.getItem("doctorId");
  const [doctorInfo, setDoctorInfo] = useState(null);
  const doctorService = new DoctorService();
  const patientServce = new PatientService();
  const fileService = new FileService();
  const formService = new FormService();
  const [files, setFiles] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctorsForms, setDoctorsForms] = useState([]);
  const [last8Patients, setLast8Patients] = useState([]);
  const [stats, setStats] = useState({
    weeklyPatients: 0,
    monthlyPatients: 0,
    activeFiles: 0
  });
  
  // Stats API state
  const statsService = useMemo(() => new StatsService(), []);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  
  // Stats filters state (entity: multi-select array; metric: always count for this chart)
  const [selectedEntities, setSelectedEntities] = useState(() => ENTITIES.map((e) => e.value));
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('last_month');
  const [selectedGroupBy, setSelectedGroupBy] = useState('day');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    const fetchDoctorInfo = async () => {
      try {
        const info = await doctorService.getDoctorInfo();
        setDoctorInfo(info);
        console.log(info);
      } catch (error) {
        console.error("Error fetching doctor info:", error);
      }
    };
    const fetchAllFiles = async () => {
      try {
        const response = await fileService.getAllFiles();
        const doctorsFiles = response.filter(
          (file) => file.created_by === doctorId
        );
        setFiles(doctorsFiles);
      } catch (error) {
        console.log("File bilgilerini çekerken bir hata oluştu:", error);
      }
    };
    const fetchAllPatients = async () => {
      try {
        const response = await patientServce.getAllPatients();
        const doctorsPatients = response.patients.filter(
          (filter) => filter.doctorId === doctorId
        );
        setPatients(doctorsPatients);
        
        // Son 8 hastayı al
        const sortedPatients = response.patients.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        const last8Patients = sortedPatients.slice(0, 8);
        setLast8Patients(last8Patients);

        // İstatistikler için hesaplamalar
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        
        const weeklyPatients = doctorsPatients.filter(
          p => new Date(p.created_at) >= weekAgo
        ).length;
        
        const monthlyPatients = doctorsPatients.filter(
          p => new Date(p.created_at) >= monthAgo
        ).length;

        setStats(prev => ({
          ...prev,
          weeklyPatients,
          monthlyPatients
        }));
      } catch (error) {
        console.log("Hastalar getirilirken bir sorun yaşandı.", error);
      }
    };
    const fetchAllForms = async () => {
      try {
        const response = await formService.getAllForms();
        const doctorsForms = response.filter(
          (filter) => filter.created_by === doctorId
        );
        setDoctorsForms(doctorsForms);
      } catch (error) {
        console.log("Hastalar getirilirken bir sorun yaşandı.", error);
      }
    };
    fetchDoctorInfo();
    fetchAllFiles();
    fetchAllPatients();
    fetchAllForms();
  }, []);

  // Fetch stats data
  const fetchStatsData = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    
    try {
      // Tarih aralığını hesapla
      let startDate, endDate;
      if (selectedTimeFilter === 'custom') {
        // Custom date range kullan
        startDate = customStartDate || '';
        endDate = customEndDate || '';
        
        // Eğer custom date range seçilmiş ama tarihler girilmemişse, hata göster
        if (!startDate || !endDate) {
          setStatsError('Please select both start and end dates for custom date range');
          setStatsLoading(false);
          setStatsData(null); // Data'yı temizle
          return;
        }
        
        // Tarih formatını kontrol et (YYYY-MM-DD formatında olmalı)
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(startDate) || !dateRegex.test(endDate)) {
          setStatsError('Invalid date format. Please use YYYY-MM-DD format');
          setStatsLoading(false);
          setStatsData(null);
          return;
        }
        
        // End date'in start date'den sonra olup olmadığını kontrol et
        if (new Date(endDate) < new Date(startDate)) {
          setStatsError('End date must be after start date');
          setStatsLoading(false);
          setStatsData(null);
          return;
        }
      } else {
        const dateRange = statsService.getDateRange(selectedTimeFilter);
        startDate = dateRange.startDate;
        endDate = dateRange.endDate;
      }
      
      // Group by'ı otomatik ayarla (eğer 'none' değilse)
      const groupBy = selectedGroupBy === 'none' 
        ? statsService.getSuggestedGroupBy(selectedTimeFilter)
        : selectedGroupBy;

      const entityParam = selectedEntities.length ? selectedEntities : ['patients'];
      const params = {
        entity: Array.isArray(entityParam) ? entityParam.join(',') : entityParam,
        metric: 'count',
        start_date: startDate,
        end_date: endDate,
        group_by: groupBy
      };

      const response = await statsService.getDoctorStats(params);
      if (response && response.data) {
        setStatsData(response);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error("Stats verileri alınırken hata:", error);
      console.error("Error details:", error.response?.data || error.message);
      setStatsError(error.response?.data?.message || error.message || "Failed to load stats");
      setStatsData(null); // Hata durumunda data'yı temizle
    } finally {
      setStatsLoading(false);
    }
  }, [selectedEntities, selectedTimeFilter, selectedGroupBy, customStartDate, customEndDate, statsService]);

  // Entity, time period veya group by değişince grafiği otomatik yenile (Filtrele'ye basmadan)
  useEffect(() => {
    if (selectedTimeFilter === 'custom' && (!customStartDate || !customEndDate)) return;
    fetchStatsData();
  }, [selectedEntities, selectedTimeFilter, selectedGroupBy, customStartDate, customEndDate, fetchStatsData]);

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Modern Stat Card Component
  const ModernStatCard = ({ title, value, icon, bgGradient, onClick }) => (
    <Card
      onClick={onClick}
      sx={{
        background: bgGradient,
        borderRadius: '16px',
        border: `1px solid ${colors.grey[800]}30`,
        transition: 'all 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          transform: 'translateY(-8px)',
          boxShadow: `0 12px 32px rgba(104, 112, 250, 0.3)`,
        } : {}
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box flex={1}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: colors.grey[300],
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 1
              }}
            >
              {title}
            </Typography>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700,
                color: colors.grey[100],
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Headeer 
        title={t('dashboardOverview')} 
        subtitle={t('yourPracticeAtAGlance')} 
      />

      {/* Stats Grid */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <ModernStatCard
            title={t('totalPatients')}
            value={patients?.length || 0}
            icon={<Diversity3Icon sx={{ fontSize: 32, color: colors.blueAccent[300] }} />}
            bgGradient={`linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.blueAccent[800]}80 100%)`}
            onClick={() => navigate('/patients')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernStatCard
            title={t('totalFiles')}
            value={files?.length || 0}
            icon={<FolderIcon sx={{ fontSize: 32, color: colors.greenAccent[300] }} />}
            bgGradient={`linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.greenAccent[800]}80 100%)`}
            onClick={() => navigate('/files')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernStatCard
            title={t('totalForms')}
            value={doctorsForms?.length || 0}
            icon={<DescriptionIcon sx={{ fontSize: 32, color: colors.grey[300] }} />}
            bgGradient={`linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.grey[800]}80 100%)`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <ModernStatCard
            title={t('subDoctors')}
            value={doctorInfo?.subDoctors?.length || 0}
            icon={<Groups2Icon sx={{ fontSize: 32, color: colors.blueAccent[200] }} />}
            bgGradient={`linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.blueAccent[700]}80 100%)`}
            onClick={() => navigate('/team')}
          />
        </Grid>
      </Grid>

      {/* Hasta Cinsiyet Dağılımı ve Son Hastalar - üstte */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Gender Chart */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              borderRadius: '16px',
              border: `1px solid ${colors.grey[800]}30`,
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700,
                  mb: 2,
                  color: colors.grey[100]
                }}
              >
                {t('patientGenderDistribution')}
              </Typography>
              <GenderPieChart patients={patients} />
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Patients */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
              borderRadius: '16px',
              border: `1px solid ${colors.grey[800]}30`,
              height: '100%',
              maxHeight: '500px',
            }}
          >
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    color: colors.grey[100]
                  }}
                >
                  {t('recentPatients')}
                </Typography>
                <Chip
                  icon={<AccessTimeIcon />}
                  label={t('last8')}
                  size="small"
                  sx={{
                    backgroundColor: `${colors.blueAccent[700]}40`,
                    color: colors.blueAccent[300],
                    fontWeight: 600,
                  }}
                />
              </Box>
              
              <Box 
                sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: `${colors.blueAccent[600]}60`,
                    borderRadius: '10px',
                  },
                }}
              >
                {last8Patients.map((patient, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      mb: 1,
                      borderRadius: '12px',
                      backgroundColor: `${colors.blueAccent[800]}20`,
                      border: `1px solid ${colors.blueAccent[700]}30`,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: `${colors.blueAccent[700]}30`,
                        transform: 'translateX(4px)',
                      }
                    }}
                    onClick={() => navigate(`/patients/${patient.id}`)}
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        background: `linear-gradient(135deg, ${colors.blueAccent[500]} 0%, ${colors.greenAccent[500]} 100%)`,
                        fontWeight: 700,
                        fontSize: '0.9rem'
                      }}
                    >
                      {patient.firstName?.charAt(0)}{patient.lastName?.charAt(0)}
                    </Avatar>
                    <Box flex={1}>
                      <Typography 
                        sx={{ 
                          fontWeight: 600,
                          color: colors.grey[100],
                          fontSize: '0.9rem'
                        }}
                      >
                        {patient.firstName} {patient.lastName}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ color: colors.grey[400] }}
                      >
                        {formatDateTime(patient.created_at)}
                      </Typography>
                    </Box>
                    <Chip
                      label={patient.fileIds?.length || 0}
                      size="small"
                      sx={{
                        backgroundColor: colors.greenAccent[700],
                        color: 'white',
                        fontWeight: 700,
                        minWidth: 32,
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Analytics & Statistics - altta */}
      <Box mb={4}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700,
            mb: 2,
            color: colors.grey[100]
          }}
        >
          {t('analyticsAndStatistics')}
        </Typography>
        
        <StatsFilters
          entity={selectedEntities}
          onEntityChange={setSelectedEntities}
          timeFilter={selectedTimeFilter}
          groupBy={selectedGroupBy}
          onTimeFilterChange={(value) => {
            setSelectedTimeFilter(value);
            if (value !== 'custom') {
              setCustomStartDate('');
              setCustomEndDate('');
            }
            const suggestedGroupBy = statsService.getSuggestedGroupBy(value);
            setSelectedGroupBy(suggestedGroupBy);
          }}
          onGroupByChange={setSelectedGroupBy}
          onRefresh={fetchStatsData}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          onCustomStartDateChange={setCustomStartDate}
          onCustomEndDateChange={setCustomEndDate}
          dashboardMode
        />

        {selectedEntities.length === 1 && statsData?.data?.[selectedEntities[0]]?.summary && (
          <StatsSummaryCards
            summary={statsData.data[selectedEntities[0]].summary}
            comparison={statsData.data[selectedEntities[0]].comparison}
            entity={selectedEntities[0]}
            metric="count"
          />
        )}

        <Card
          sx={{
            background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
            borderRadius: '16px',
            border: `1px solid ${colors.grey[800]}30`,
            mb: 3
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                mb: 3,
                color: colors.grey[100]
              }}
            >
              {selectedEntities.length === 1
                ? (t(`stats:entities.${selectedEntities[0]}`) || selectedEntities[0])
                : selectedEntities.map((e) => t(`stats:entities.${e}`) || e).join(', ')}{' '}
              - {t('stats:metrics.count') || 'Toplam Sayı'}
            </Typography>
            <StatsChart
              data={statsData?.data}
              loading={statsLoading}
              error={statsError}
              metric="count"
              entity={selectedEntities.length === 1 ? selectedEntities[0] : null}
              entities={selectedEntities}
              files={files}
              period={selectedTimeFilter}
              groupBy={selectedGroupBy === 'none' 
                ? statsService.getSuggestedGroupBy(selectedTimeFilter)
                : selectedGroupBy}
            />
          </CardContent>
        </Card>
      </Box>

      {/* Quick Actions */}
      <Box mt={3}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700,
            mb: 2,
            color: colors.grey[100]
          }}
        >
          {t('quickActions')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              onClick={() => navigate('/AddPatient')}
              sx={{
                background: `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: `0 8px 24px ${colors.blueAccent[500]}50`,
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <PersonAddIcon sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  {t('addPatient')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              onClick={() => navigate('/files')}
              sx={{
                background: `linear-gradient(135deg, ${colors.greenAccent[600]} 0%, ${colors.greenAccent[700]} 100%)`,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: `0 8px 24px ${colors.greenAccent[500]}50`,
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <FolderIcon sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  {t('createFile')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              onClick={() => navigate('/inviteDoctor')}
              sx={{
                background: `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.greenAccent[600]} 100%)`,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: `0 8px 24px ${colors.blueAccent[400]}50`,
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Groups2Icon sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  {t('inviteDoctor')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              onClick={() => navigate('/files')}
              sx={{
                background: `linear-gradient(135deg, ${colors.grey[700]} 0%, ${colors.grey[800]} 100%)`,
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: `0 8px 24px ${colors.grey[600]}50`,
                }
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <DescriptionIcon sx={{ fontSize: 48, color: 'white', mb: 1 }} />
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                  {t('viewFiles')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
